// src/pages/GestaoOficinas.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import {
    FaWrench, FaCheckCircle, FaTimesCircle, FaSearch,
    FaSpinner, FaFileExcel, FaUpload, FaPlus, FaExclamationTriangle,
    FaEdit, FaBuilding, FaCog, FaBan,
    FaDownload, FaCheckSquare, FaSquare, FaTrash, FaCheck, FaLock,
    FaChevronLeft, FaChevronRight, FaStopCircle, FaPauseCircle, FaBell,
    FaUsers, FaUserCheck, FaClock, FaPaperPlane,
    FaStar, FaRegStar, FaTimes, FaPhone, FaWhatsapp, FaEnvelope, FaBars,
    FaMagic, FaFilter, FaCalendarAlt, FaDollarSign, FaTag, FaChevronDown,
} from 'react-icons/fa';
import './GestaoOficinas.css';
import MaintenanceActionModal from '../components/MaintenanceActionModal';

const POLL_TABLE_MS = 30000;
const MAX_TELEFONES = 5;

const FILTROS_INIT = {
    placa: '', status: '', tipo_servico: '',
    oficina_nome: '', data_inicio: '', data_fim: '',
    custo_min: '', custo_max: '',
};

const STATUS_LABELS = {
    aguardando_orcamento: 'Em Cotação',
    pendente: 'Suspenso',
    em_analise: 'Em Análise',
    aprovado: 'Em Execução',
    recusado: 'Reprovado',
    concluido: 'Finalizado',
};

const MODELO_MANUTENCAO_HEADERS = [
    'Anexos','Unidade organizacional','Veículo','Grupo da unidade rastreada',
    'Modelo do veículo','Tipo da manutenção','Nome da etapa da manutenção','Situação',
    'Km planejado','Km real','Data planejada','Data realização',
    'Horímetro planejado','Horímetro real','Tempo da manutenção',
    'Itens previstos','Itens não previstos','Itens cancelados','Itens migrados','Itens realizados',
    'Custo peças planejadas','Custo serviços planejados','Custo total planejado',
    'Custo pecas realizadas','Custo serviços realizados','Custo total realizado',
    'Custo realização peças planejadas','Custo realização serviços planejados',
    'Custo realização peças NÃO planejadas','Custo realização serviços NÃO planejados',
    'Observação da manutenção',
];

const formatCurrency = (v) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const fmtDateBR = (iso) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
};

const extrairTelefones = (item) => {
    let arr = item?.telefones_json;
    if (typeof arr === 'string' && arr.trim()) { try { arr = JSON.parse(arr); } catch { arr = null; } }
    if (Array.isArray(arr) && arr.length) return arr.filter(Boolean);
    if (item?.telefone) return [item.telefone];
    return [];
};

const telefonePrioritario = (item) => { const t = extrairTelefones(item); return t.length ? t[0] : null; };

const temContatoNoCanal = (item, canal) =>
    canal === 'whatsapp' ? !!telefonePrioritario(item) : !!(item?.email && String(item.email).trim());

// ─── Componentes básicos ───────────────────────────────────────────────────────

const CompanyAvatar = ({ name }) => {
    const initials = name ? name.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase() : 'OF';
    const colors = ['#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899'];
    return <div className="go-company-avatar" style={{ backgroundColor: colors[(name?.length || 0) % colors.length] }}>{initials}</div>;
};

const StatusBadge = ({ status }) => {
    const config = {
        aguardando_orcamento: { style: 'warning', label: 'Em Cotação',  icon: <FaSpinner className="go-spin-icon"/> },
        pendente:             { style: 'warning', label: 'Suspenso',    icon: <FaPauseCircle/> },
        em_analise:           { style: 'info',    label: 'Em Análise',  icon: <FaSearch/> },
        aprovado:             { style: 'success', label: 'Em Execução', icon: <FaWrench/> },
        recusado:             { style: 'danger',  label: 'Reprovado',   icon: <FaStopCircle/> },
        concluido:            { style: 'dark',    label: 'Finalizado',  icon: <FaCheckCircle/> },
        true:  { style: 'success', label: 'Ativo',     icon: <FaCheckCircle/> },
        false: { style: 'danger',  label: 'Bloqueado', icon: <FaBan/> },
        1: { style: 'success', label: 'Ativo',     icon: <FaCheckCircle/> },
        0: { style: 'danger',  label: 'Bloqueado', icon: <FaBan/> },
    };
    let key = status;
    if (status === true  || status === 1)  key = true;
    if (status === false || status === 0)  key = false;
    const current = config[key] || { style: 'default', label: status, icon: null };
    return <span className={`go-status-badge go-badge-${current.style}`}>{current.icon} {current.label}</span>;
};

function AcoesMenu({ children, label = 'Ações' }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDoc);
        document.addEventListener('keydown', onEsc);
        return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onEsc); };
    }, [open]);
    return (
        <div className="go-acoes-menu" ref={ref}>
            <button type="button" className={`go-btn-new outline ${open ? 'go-acoes-open' : ''}`} onClick={() => setOpen(o => !o)}>
                <FaBars /> {label}
            </button>
            {open && (<div className="go-acoes-dropdown" onClick={() => setOpen(false)}>{children}</div>)}
        </div>
    );
}

function CanalSwitch({ canal, onChange }) {
    return (
        <div className="canal-switch" role="tablist">
            <button type="button" role="tab" aria-selected={canal === 'email'} className={`canal-switch-pill ${canal === 'email' ? 'active email' : ''}`} onClick={() => onChange('email')}><FaEnvelope /> E-mail</button>
            <button type="button" role="tab" aria-selected={canal === 'whatsapp'} className={`canal-switch-pill ${canal === 'whatsapp' ? 'active whatsapp' : ''}`} onClick={() => onChange('whatsapp')}><FaWhatsapp /> WhatsApp</button>
        </div>
    );
}

function OficinaSelector({ oficinas, selected, onChange, canal = 'email' }) {
    const [modo, setModo] = useState('nenhuma');
    const [busca, setBusca] = useState('');
    const ativas    = oficinas.filter(o => o.ativo === 1 || o.ativo === true);
    const filtradas = ativas.filter(o => o.nome_fantasia.toLowerCase().includes(busca.toLowerCase()));
    const elegiveis = ativas.filter(o => temContatoNoCanal(o, canal));
    const semContatoQtd = ativas.length - elegiveis.length;
    const canalLabel   = canal === 'whatsapp' ? 'WhatsApp' : 'e-mails';
    const contatoLabel = canal === 'whatsapp' ? 'telefone' : 'e-mail';

    const toggleOficina = (id, ok) => {
        if (!ok) return;
        const s = new Set(selected);
        s.has(id) ? s.delete(id) : s.add(id);
        onChange(s);
    };
    const handleModo = (m) => {
        setModo(m);
        if (m === 'nenhuma')    onChange(new Set());
        if (m === 'todas')      onChange(new Set(elegiveis.map(o => o.id)));
        if (m === 'individual') onChange(new Set());
    };
    useEffect(() => {
        if (modo === 'nenhuma') return;
        const ok = new Set(elegiveis.map(o => o.id));
        const limpo = new Set([...selected].filter(id => ok.has(id)));
        if (limpo.size !== selected.size) onChange(limpo);
        if (modo === 'todas') onChange(new Set(elegiveis.map(o => o.id)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canal]);

    return (
        <div className="of-selector">
            <div className="of-mode-pills">
                <button type="button" className={`of-mode-pill ${modo === 'nenhuma' ? 'active' : ''}`} onClick={() => handleModo('nenhuma')}><FaClock /> Cotar depois</button>
                <button type="button" className={`of-mode-pill ${modo === 'individual' ? 'active' : ''}`} onClick={() => handleModo('individual')}><FaUserCheck /> Selecionar</button>
                <button type="button" className={`of-mode-pill ${modo === 'todas' ? 'active danger' : ''}`} onClick={() => handleModo('todas')}><FaUsers /> Todas ({elegiveis.length})</button>
            </div>
            {(modo === 'individual' || modo === 'todas') && semContatoQtd > 0 && (
                <div className="of-canal-warn"><FaExclamationTriangle /><span><strong>{semContatoQtd}</strong> parceiro(s) sem {contatoLabel} não podem receber por {canalLabel}.</span></div>
            )}
            {(modo === 'individual' || modo === 'todas') && (
                <div className="of-list-wrapper">
                    <div className="of-list-header">
                        <div className="of-search"><FaSearch /><input type="text" placeholder="Buscar oficina..." value={busca} onChange={e => setBusca(e.target.value)} /></div>
                        <button type="button" className="of-select-all-btn" onClick={() => {
                            const ef = filtradas.filter(o => temContatoNoCanal(o, canal));
                            const allSel = ef.length > 0 && ef.every(o => selected.has(o.id));
                            onChange(allSel ? new Set() : new Set(ef.map(o => o.id)));
                        }}>
                            {filtradas.filter(o => temContatoNoCanal(o, canal)).every(o => selected.has(o.id)) && filtradas.some(o => temContatoNoCanal(o, canal)) ? <FaCheckSquare /> : <FaSquare />}
                            {filtradas.filter(o => temContatoNoCanal(o, canal)).every(o => selected.has(o.id)) && filtradas.some(o => temContatoNoCanal(o, canal)) ? ' Desmarcar' : ' Marcar todos'}
                        </button>
                    </div>
                    <div className="of-list">
                        {filtradas.length === 0 ? <div className="of-empty">Nenhuma oficina encontrada.</div> : filtradas.map(of => {
                            const ok = temContatoNoCanal(of, canal);
                            const sel = selected.has(of.id);
                            const contato = canal === 'whatsapp' ? telefonePrioritario(of) : of.email;
                            return (
                                <div key={of.id} className={`of-item ${sel ? 'of-item--selected' : ''} ${!ok ? 'of-item--disabled' : ''}`} onClick={() => toggleOficina(of.id, ok)}>
                                    <div className={`of-checkbox ${sel ? 'of-checkbox--checked' : ''}`}>{sel && <FaCheck />}</div>
                                    <div className="of-item-info"><span className="of-item-name">{of.nome_fantasia}</span>{of.especialidade && <span className="of-item-esp">{of.especialidade}</span>}</div>
                                    {ok ? <span className="of-item-contato">{canal === 'whatsapp' ? <FaWhatsapp /> : <FaEnvelope />} {contato}</span>
                                        : <span className="of-item-sem-contato"><FaExclamationTriangle /> sem {contatoLabel}</span>}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
            {selected.size > 0 && (
                <div className={`of-summary ${canal === 'whatsapp' ? 'of-summary--wa' : ''}`}>
                    {canal === 'whatsapp' ? <FaWhatsapp style={{ color: '#16a34a' }} /> : <FaEnvelope style={{ color: '#2563eb' }} />}
                    <span><strong>{selected.size}</strong> oficina{selected.size > 1 ? 's' : ''} selecionada{selected.size > 1 ? 's' : ''} — {canal === 'whatsapp' ? 'WhatsApp' : 'e-mails'} disparad{canal === 'whatsapp' ? 'os' : 'os'} ao salvar</span>
                </div>
            )}
        </div>
    );
}

// ══════════════════════════════════════════════════════════════════════════════
// SMART FILTRO BAR — busca por IA (Gemini com fallbacks) + filtros manuais
// ══════════════════════════════════════════════════════════════════════════════

const CHIP_CORES = {
    placa:        { bg: '#eff6ff', border: '#bfdbfe', text: '#1d4ed8' },
    status:       { bg: '#f5f3ff', border: '#ddd6fe', text: '#6d28d9' },
    tipo_servico: { bg: '#f0fdf4', border: '#bbf7d0', text: '#15803d' },
    oficina_nome: { bg: '#fffbeb', border: '#fde68a', text: '#92400e' },
    data:         { bg: '#fdf4ff', border: '#f0abfc', text: '#86198f' },
    custo:        { bg: '#ecfeff', border: '#a5f3fc', text: '#155e75' },
};

function FiltroChip({ tipo, label, value, onRemove }) {
    const c = CHIP_CORES[tipo] || CHIP_CORES.placa;
    return (
        <span className="sfb-chip" style={{ background: c.bg, border: `1px solid ${c.border}`, color: c.text }}>
            <span className="sfb-chip-txt">{label}: <strong>{value}</strong></span>
            <button className="sfb-chip-x" onClick={onRemove}><FaTimes /></button>
        </span>
    );
}

function SmartFiltroBar({ filtros, onFiltrosChange, onLimpar, iaQuery, onIAQueryChange, onInterpretarIA, loadingIA, resumoIA, erroIA }) {
    const [painelAberto, setPainelAberto] = useState(false);
    const ref = useRef(null);

    const qtd = Object.values(filtros).filter(v => v !== '').length;

    useEffect(() => {
        if (!painelAberto) return;
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setPainelAberto(false); };
        const onEsc = (e) => { if (e.key === 'Escape') setPainelAberto(false); };
        document.addEventListener('mousedown', onDoc);
        document.addEventListener('keydown', onEsc);
        return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onEsc); };
    }, [painelAberto]);

    const set = (campo, val) => onFiltrosChange({ ...filtros, [campo]: val });

    // Chips
    const chips = [];
    if (filtros.placa)        chips.push({ tipo: 'placa',        label: 'Placa',   value: filtros.placa,                                   campo: 'placa' });
    if (filtros.status)       chips.push({ tipo: 'status',       label: 'Status',  value: STATUS_LABELS[filtros.status] || filtros.status,  campo: 'status' });
    if (filtros.tipo_servico) chips.push({ tipo: 'tipo_servico', label: 'Serviço', value: filtros.tipo_servico,                             campo: 'tipo_servico' });
    if (filtros.oficina_nome) chips.push({ tipo: 'oficina_nome', label: 'Oficina', value: filtros.oficina_nome,                             campo: 'oficina_nome' });
    if (filtros.data_inicio || filtros.data_fim) {
        const p = [filtros.data_inicio && fmtDateBR(filtros.data_inicio), filtros.data_fim && fmtDateBR(filtros.data_fim)].filter(Boolean).join(' → ');
        chips.push({ tipo: 'data', label: 'Período', value: p, campo: '__data' });
    }
    if (filtros.custo_min !== '' || filtros.custo_max !== '') {
        const p = [filtros.custo_min && `R$ ${filtros.custo_min}`, filtros.custo_max && `R$ ${filtros.custo_max}`].filter(Boolean).join(' – ');
        chips.push({ tipo: 'custo', label: 'Custo', value: p, campo: '__custo' });
    }
    const remover = (campo) => {
        if (campo === '__data')  return onFiltrosChange({ ...filtros, data_inicio: '', data_fim: '' });
        if (campo === '__custo') return onFiltrosChange({ ...filtros, custo_min: '', custo_max: '' });
        set(campo, '');
    };

    return (
        <div className="sfb-root">
            <div className="sfb-bar">
                {/* Campo de IA */}
                <div className={`sfb-ia-wrap${loadingIA ? ' sfb-ia-loading' : ''}${erroIA ? ' sfb-ia-erro' : ''}${resumoIA && !erroIA ? ' sfb-ia-ok' : ''}`}>
                    <span className={`sfb-ia-glyph${loadingIA ? ' sfb-spin' : ''}`}>
                        {loadingIA ? <FaSpinner /> : <FaMagic />}
                    </span>
                    <input
                        className="sfb-ia-input"
                        placeholder="Busca inteligente: ex. 'placa ABC-1234 em cotação este mês'"
                        value={iaQuery}
                        onChange={e => onIAQueryChange(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && iaQuery.trim()) onInterpretarIA(); }}
                        disabled={loadingIA}
                    />
                    {iaQuery.trim() && !loadingIA && (
                        <button className="sfb-ia-send" onClick={onInterpretarIA}>
                            <FaPaperPlane /> Filtrar
                        </button>
                    )}
                    {loadingIA && <span className="sfb-ia-loading-txt">Analisando...</span>}
                    {(iaQuery || resumoIA) && !loadingIA && (
                        <button className="sfb-ia-clear-btn" onClick={() => { onIAQueryChange(''); onLimpar(); }} title="Limpar">
                            <FaTimesCircle />
                        </button>
                    )}
                </div>

                {/* Botão filtros manuais */}
                <div className="sfb-filtros-wrap" ref={ref}>
                    <button
                        className={`sfb-filtros-btn${qtd > 0 ? ' sfb-filtros-btn--ativo' : ''}${painelAberto ? ' sfb-filtros-btn--open' : ''}`}
                        onClick={() => setPainelAberto(o => !o)}
                    >
                        <FaFilter />
                        <span>Filtros</span>
                        {qtd > 0 && <span className="sfb-qtd-badge">{qtd}</span>}
                        <FaChevronDown className={`sfb-caret${painelAberto ? ' sfb-caret--up' : ''}`} />
                    </button>

                    {painelAberto && (
                        <div className="sfb-panel">
                            <div className="sfb-panel-head">
                                <span className="sfb-panel-title"><FaFilter /> Filtros Avançados</span>
                                {qtd > 0 && <button className="sfb-panel-limpar" onClick={() => { onLimpar(); setPainelAberto(false); }}><FaTimes /> Limpar todos</button>}
                            </div>
                            <div className="sfb-panel-grid">
                                <div className="sfb-field">
                                    <label className="sfb-label"><FaSearch /> Placa</label>
                                    <input className="sfb-input" placeholder="ABC-1234" value={filtros.placa} onChange={e => set('placa', e.target.value.toUpperCase())} />
                                </div>
                                <div className="sfb-field">
                                    <label className="sfb-label"><FaWrench /> Status</label>
                                    <select className="sfb-select" value={filtros.status} onChange={e => set('status', e.target.value)}>
                                        <option value="">Todos os status</option>
                                        <option value="aguardando_orcamento">Em Cotação</option>
                                        <option value="pendente">Suspenso</option>
                                        <option value="em_analise">Em Análise</option>
                                        <option value="aprovado">Em Execução</option>
                                        <option value="recusado">Reprovado</option>
                                        <option value="concluido">Finalizado</option>
                                    </select>
                                </div>
                                <div className="sfb-field">
                                    <label className="sfb-label"><FaTag /> Tipo de Serviço</label>
                                    <input className="sfb-input" placeholder="Ex: troca de óleo" value={filtros.tipo_servico} onChange={e => set('tipo_servico', e.target.value)} />
                                </div>
                                <div className="sfb-field">
                                    <label className="sfb-label"><FaBuilding /> Parceiro / Oficina</label>
                                    <input className="sfb-input" placeholder="Nome da oficina" value={filtros.oficina_nome} onChange={e => set('oficina_nome', e.target.value)} />
                                </div>
                                <div className="sfb-field">
                                    <label className="sfb-label"><FaCalendarAlt /> Abertura — De</label>
                                    <input className="sfb-input" type="date" value={filtros.data_inicio} onChange={e => set('data_inicio', e.target.value)} />
                                </div>
                                <div className="sfb-field">
                                    <label className="sfb-label"><FaCalendarAlt /> Abertura — Até</label>
                                    <input className="sfb-input" type="date" value={filtros.data_fim} onChange={e => set('data_fim', e.target.value)} />
                                </div>
                                <div className="sfb-field">
                                    <label className="sfb-label"><FaDollarSign /> Custo Mínimo (R$)</label>
                                    <input className="sfb-input" type="number" min="0" step="0.01" placeholder="0,00" value={filtros.custo_min} onChange={e => set('custo_min', e.target.value)} />
                                </div>
                                <div className="sfb-field">
                                    <label className="sfb-label"><FaDollarSign /> Custo Máximo (R$)</label>
                                    <input className="sfb-input" type="number" min="0" step="0.01" placeholder="Sem limite" value={filtros.custo_max} onChange={e => set('custo_max', e.target.value)} />
                                </div>
                            </div>
                            <div className="sfb-panel-foot">
                                <button className="sfb-panel-apply" onClick={() => setPainelAberto(false)}>
                                    <FaCheckCircle /> Aplicar{qtd > 0 ? ` (${qtd} ativo${qtd > 1 ? 's' : ''})` : ''}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Chips de filtros ativos */}
            {chips.length > 0 && (
                <div className="sfb-chips-row">
                    <span className="sfb-chips-label">Ativos:</span>
                    {chips.map((c, i) => <FiltroChip key={i} tipo={c.tipo} label={c.label} value={c.value} onRemove={() => remover(c.campo)} />)}
                    <button className="sfb-clear-all" onClick={onLimpar}><FaTimes /> Limpar tudo</button>
                </div>
            )}

            {/* Banner IA */}
            {resumoIA && !erroIA && (
                <div className="sfb-banner sfb-banner--ok">
                    <FaMagic /> <span>{resumoIA}</span>
                </div>
            )}
            {erroIA && (
                <div className="sfb-banner sfb-banner--erro">
                    <FaExclamationTriangle /> <span>{erroIA} — Use os filtros manuais acima.</span>
                </div>
            )}
        </div>
    );
}

// ══════════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ══════════════════════════════════════════════════════════════════════════════
export default function GestaoOficinas() {
    const [loadingManut,          setLoadingManut]          = useState(false);
    const [loadingOf,             setLoadingOf]             = useState(false);
    const [manutencoes,           setManutencoes]           = useState([]);
    const [oficinas,              setOficinas]              = useState([]);
    const [listaCompletaOficinas, setListaCompletaOficinas] = useState([]);
    const [unreadByManutencao,    setUnreadByManutencao]    = useState({});

    const [page,       setPage]       = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const LIMIT = 10;

    const LIMIT_MANUT = 20;
    const [pageManut,       setPageManut]       = useState(1);
    const [totalPagesManut, setTotalPagesManut] = useState(1);
    const [statsManut,      setStatsManut]      = useState({ abertos: 0, pendentes: 0 });

    const [filtro,          setFiltro]          = useState('');
    const [filtroDebounced, setFiltroDebounced] = useState('');
    const [filtrosAvancados, setFiltrosAvancados] = useState(FILTROS_INIT);

    // IA
    const [iaQuery,   setIaQuery]   = useState('');
    const [iaResumo,  setIaResumo]  = useState('');
    const [iaErro,    setIaErro]    = useState('');
    const [loadingIA, setLoadingIA] = useState(false);

    const [activeTab,         setActiveTab]         = useState('manutencoes');
    const [modalConfig,       setModalConfig]       = useState({ type: null, data: null });
    const [selectedOficinas,  setSelectedOficinas]  = useState([]);
    const [confirmDeleteText, setConfirmDeleteText] = useState('');

    const [formManutencao,         setFormManutencao]         = useState({ veiculo_id: '', tipo_servico: '', descricao_problema: '' });
    const [formManutencaoOficinas, setFormManutencaoOficinas] = useState(new Set());
    const [formManutencaoCanal,    setFormManutencaoCanal]    = useState('email');
    const [savingManutencao,       setSavingManutencao]       = useState(false);

    const [formFornecedor, setFormFornecedor] = useState({
        nome_fantasia:'',razao_social:'',cnpj:'',email:'',telefone:'',
        especialidade:'',cep:'',logradouro:'',numero:'',complemento:'',bairro:'',cidade:'',estado:'',
    });
    const [telefonesForm,  setTelefonesForm]  = useState(['']);
    const [telPrioritario, setTelPrioritario] = useState(0);
    const [arquivoImportacao,  setArquivoImportacao]  = useState(null);
    const [arquivoImportManut, setArquivoImportManut] = useState(null);
    const [importManutResult,  setImportManutResult]  = useState(null);
    const [importingManut,     setImportingManut]     = useState(false);

    const addTelefone    = () => setTelefonesForm(p => p.length >= MAX_TELEFONES ? p : [...p, '']);
    const updateTelefone = (i, v) => setTelefonesForm(p => p.map((t, idx) => idx === i ? v : t));
    const removeTelefone = (i) => {
        setTelefonesForm(p => { const n = p.filter((_, idx) => idx !== i); return n.length ? n : ['']; });
        setTelPrioritario(p => i === p ? 0 : i < p ? p - 1 : p);
    };

    // ── IA: interpreta linguagem natural ──────────────────────────────────────
    const handleInterpretarIA = useCallback(async () => {
        if (!iaQuery.trim()) return;
        setLoadingIA(true); setIaErro(''); setIaResumo('');
        try {
            const res = await api.post('/api/frota/manutencoes/interpretar-filtro', { query: iaQuery.trim() });
            const { filtros, resumo } = res.data;
            setFiltrosAvancados(prev => ({
                ...prev,
                placa:        filtros.placa        || '',
                status:       filtros.status       || '',
                tipo_servico: filtros.tipo_servico || '',
                oficina_nome: filtros.oficina_nome || '',
                data_inicio:  filtros.data_inicio  || '',
                data_fim:     filtros.data_fim      || '',
                custo_min:    filtros.custo_min != null ? String(filtros.custo_min) : '',
                custo_max:    filtros.custo_max != null ? String(filtros.custo_max) : '',
            }));
            setIaResumo(resumo || 'Filtros aplicados pela IA.');
            setPageManut(1);
            toast.success('Filtros aplicados com IA!');
        } catch (err) {
            const msg = err.response?.data?.error || 'IA indisponível no momento.';
            setIaErro(msg);
            if (err.response?.data?.fallback) toast.warning('IA indisponível — use os filtros manuais.');
            else toast.error(msg);
        } finally { setLoadingIA(false); }
    }, [iaQuery]);

    const limparFiltros = useCallback(() => {
        setFiltrosAvancados(FILTROS_INIT);
        setIaQuery(''); setIaResumo(''); setIaErro('');
        setPageManut(1);
    }, []);

    const fetchAllUnreadCounts = useCallback(async (lista) => {
        if (!lista?.length) return;
        try {
            const res = await api.get('/api/frota/manutencoes/unread-summary');
            if (res.data && typeof res.data === 'object') setUnreadByManutencao(res.data);
        } catch {}
    }, []);

    const fetchManutencoes = useCallback(async () => {
        setLoadingManut(true);
        try {
            const p = new URLSearchParams({ page: pageManut, limit: LIMIT_MANUT });
            if (filtroDebounced.trim())        p.set('search',       filtroDebounced.trim());
            if (filtrosAvancados.placa)        p.set('placa',        filtrosAvancados.placa);
            if (filtrosAvancados.status)       p.set('status',       filtrosAvancados.status);
            if (filtrosAvancados.tipo_servico) p.set('tipo_servico', filtrosAvancados.tipo_servico);
            if (filtrosAvancados.oficina_nome) p.set('oficina_nome', filtrosAvancados.oficina_nome);
            if (filtrosAvancados.data_inicio)  p.set('data_inicio',  filtrosAvancados.data_inicio);
            if (filtrosAvancados.data_fim)     p.set('data_fim',     filtrosAvancados.data_fim);
            if (filtrosAvancados.custo_min)    p.set('custo_min',    filtrosAvancados.custo_min);
            if (filtrosAvancados.custo_max)    p.set('custo_max',    filtrosAvancados.custo_max);

            const res   = await api.get(`/api/frota/manutencoes?${p.toString()}`);
            const lista = Array.isArray(res.data) ? res.data : (res.data?.data || []);
            setManutencoes(lista);
            if (res.data?.pagination) setTotalPagesManut(res.data.pagination.totalPages || 1);
            if (res.data?.stats)      setStatsManut(res.data.stats);
            fetchAllUnreadCounts(lista);
        } catch (err) { console.error(err); toast.error('Erro ao carregar manutenções.'); }
        finally { setLoadingManut(false); }
    }, [pageManut, filtroDebounced, filtrosAvancados, fetchAllUnreadCounts]);

    const fetchOficinas = useCallback(async () => {
        setLoadingOf(true);
        try {
            const [r1, r2] = await Promise.all([
                api.get(`/api/oficinas?page=${page}&limit=${LIMIT}`),
                api.get('/api/oficinas?limit=10000'),
            ]);
            if (r1.data?.data) { setOficinas(r1.data.data); if (r1.data.pagination) setTotalPages(r1.data.pagination.totalPages); }
            else if (Array.isArray(r1.data)) { setOficinas(r1.data); setTotalPages(1); }
            else setOficinas([]);
            if (r2.data?.data)               setListaCompletaOficinas(r2.data.data);
            else if (Array.isArray(r2.data)) setListaCompletaOficinas(r2.data);
            setSelectedOficinas([]);
        } catch (err) { console.error(err); toast.error('Erro ao carregar parceiros.'); }
        finally { setLoadingOf(false); }
    }, [page]);

    const fetchData = useCallback(async () => { await Promise.all([fetchManutencoes(), fetchOficinas()]); }, [fetchManutencoes, fetchOficinas]);

    useEffect(() => { fetchManutencoes(); }, [fetchManutencoes]);
    useEffect(() => { fetchOficinas();   }, [fetchOficinas]);
    useEffect(() => { const t = setTimeout(() => { setFiltroDebounced(filtro); setPageManut(1); }, 400); return () => clearTimeout(t); }, [filtro]);
    useEffect(() => { setPageManut(1); }, [filtrosAvancados]);
    useEffect(() => {
        if (!manutencoes.length) return;
        const t = setInterval(() => fetchAllUnreadCounts(manutencoes), POLL_TABLE_MS);
        return () => clearInterval(t);
    }, [manutencoes, fetchAllUnreadCounts]);

    const handleCloseModal = useCallback(() => {
        if (modalConfig.data?.id) setUnreadByManutencao(prev => ({ ...prev, [modalConfig.data.id]: 0 }));
        setModalConfig({ type: null, data: null });
    }, [modalConfig.data]);

    const handleAction = async (fn, msg) => {
        try { await fn(); toast.success(msg); fetchData(); setModalConfig({ type: null, data: null }); }
        catch { toast.error('Erro na operação.'); }
    };

    const handleDownloadTemplate = () => {
        const header = ['NomeFantasia','RazaoSocial','CNPJ','Email','Telefone','Especialidade','CEP','Logradouro','Numero','Complemento','Bairro','Cidade','Estado'];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet([{NomeFantasia:'Oficina Exemplo (apague)',RazaoSocial:'Oficina Ltda',CNPJ:'00.000.000/0001-00',Email:'contato@ex.com',Telefone:'(11) 99999-0001, (11) 3333-0002',Especialidade:'Funilaria',CEP:'00000-000',Logradouro:'Rua Ex',Numero:'100',Complemento:'',Bairro:'Centro',Cidade:'São Paulo',Estado:'SP'}], { header }), 'Modelo');
        XLSX.writeFile(wb, 'Modelo_Parceiros.xlsx');
    };

    const handleDownloadModeloManutencao = () => {
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet([{
            'Anexos':'','Unidade organizacional':'_DM_','Veículo':'SSX6G60 - Carlos (apague)','Grupo da unidade rastreada':'Equipe','Modelo do veículo':'CHEV/ONIX','Tipo da manutenção':'Preventiva','Nome da etapa da manutenção':'40000','Situação':'Realizada','Km planejado':'40000','Km real':'40302','Data planejada':'','Data realização':'02/02/2026','Horímetro planejado':'','Horímetro real':'','Tempo da manutenção':'03:21:30','Itens previstos':'8','Itens não previstos':'0','Itens cancelados':'5','Itens migrados':'0','Itens realizados':'3','Custo peças planejadas':'R$ 1.696,58','Custo serviços planejados':'R$ 359,50','Custo total planejado':'R$ 2.056,08','Custo pecas realizadas':'R$ 407,56','Custo serviços realizados':'R$ 350,00','Custo total realizado':'R$ 757,56','Custo realização peças planejadas':'R$ 1.584,44','Custo realização serviços planejados':'R$ 399,70','Custo realização peças NÃO planejadas':'R$ 0,00','Custo realização serviços NÃO planejados':'R$ 0,00','Observação da manutenção':'Manutenção preventiva 40.302km 02/02/2026',
        }], { header: MODELO_MANUTENCAO_HEADERS }), 'Manutenções');
        XLSX.writeFile(wb, 'Modelo_Importacao_Manutencoes.xlsx');
    };

    const abrirImportManutencao = () => { setArquivoImportManut(null); setImportManutResult(null); setModalConfig({ type: 'importacao_manutencao', data: null }); };

    const submitImportManutencao = async () => {
        if (!arquivoImportManut) return toast.warning('Selecione um arquivo.');
        setImportingManut(true);
        try {
            const fd = new FormData(); fd.append('file', arquivoImportManut);
            const res = await api.post('/api/frota/manutencoes/importar', fd);
            setImportManutResult(res.data);
            toast.success(res.data.message || 'Importação concluída.');
            fetchData();
        } catch (err) { toast.error(err.response?.data?.error || 'Erro ao importar manutenções.'); }
        finally { setImportingManut(false); }
    };

    const submitManutencao = async (e) => {
        e.preventDefault(); setSavingManutencao(true);
        try {
            let mid = modalConfig.data?.id;
            if (mid) {
                await api.put(`/api/frota/manutencoes/${mid}`, { tipo_servico: formManutencao.tipo_servico, descricao_problema: formManutencao.descricao_problema });
                toast.success('Atualizado!');
            } else {
                const res = await api.post('/api/frota/manutencoes', { veiculo_id: formManutencao.veiculo_id, fornecedor_id: formManutencaoOficinas.size === 1 ? [...formManutencaoOficinas][0] : null, tipo_servico: formManutencao.tipo_servico, descricao_problema: formManutencao.descricao_problema });
                mid = res.data.id;
                if (formManutencaoOficinas.size > 0 && mid) {
                    const fd = new FormData();
                    fd.append('canal', formManutencaoCanal);
                    fd.append('oficinas_ids', JSON.stringify([...formManutencaoOficinas]));
                    fd.append('subject', `Solicitação de Orçamento — ${formManutencao.tipo_servico} (${formManutencao.veiculo_id})`);
                    fd.append('message', `Olá parceiro,\n\nSolicitamos orçamento para o veículo ${formManutencao.veiculo_id}.\n\nServiço: ${formManutencao.tipo_servico}\n\nDescrição:\n${formManutencao.descricao_problema || 'Sem descrição adicional.'}\n\nFavor responder com valores e prazo.\n\nAtenciosamente,\nEquipe de Frota`);
                    try {
                        const dr = await api.post(`/api/frota/manutencoes/${mid}/solicitar-cotacao`, fd);
                        toast.success(`Solicitação criada e ${dr.data.message || `cotação disparada para ${formManutencaoOficinas.size} parceiro(s)!`}`);
                    } catch (de) {
                        toast.warning(`Manutenção criada, mas falha ao disparar: ` + (de.response?.data?.error || de.message));
                    }
                } else { toast.success('Solicitação criada com sucesso!'); }
            }
            fetchData(); setModalConfig({ type: null, data: null });
        } catch (err) { toast.error(err.response?.data?.error || 'Erro ao salvar solicitação.'); }
        finally { setSavingManutencao(false); }
    };

    const submitFornecedor = (e) => {
        e.preventDefault();
        const pv = (telefonesForm[telPrioritario] || '').trim();
        const tels = telefonesForm.map(t => t.trim()).filter(Boolean);
        let pr = tels.indexOf(pv); if (pr < 0) pr = 0;
        const payload = { ...formFornecedor, telefones: tels, telefone_prioritario: pr };
        if (modalConfig.data?.id) handleAction(() => api.put(`/api/oficinas/${modalConfig.data.id}`, payload), 'Parceiro atualizado!');
        else handleAction(() => api.post('/api/oficinas', payload), 'Parceiro cadastrado!');
    };

    const submitImportacao = () => {
        if (!arquivoImportacao) return toast.warning('Selecione um arquivo.');
        const fd = new FormData(); fd.append('file', arquivoImportacao);
        handleAction(() => api.post('/api/oficinas/importar', fd), 'Importação iniciada.');
    };

    const buscarCep = async (cep) => {
        const l = cep.replace(/\D/g, '');
        if (l.length !== 8) return;
        try {
            const res = await fetch(`https://viacep.com.br/ws/${l}/json/`);
            const d = await res.json();
            if (!d.erro) { setFormFornecedor(p => ({ ...p, logradouro: d.logradouro||p.logradouro, bairro: d.bairro||p.bairro, cidade: d.localidade||p.cidade, estado: d.uf||p.estado })); toast.success('Endereço preenchido!'); }
            else toast.warning('CEP não encontrado.');
        } catch { toast.error('Erro na busca do CEP.'); }
    };

    const handleToggleStatus = async (o) => {
        const novo = !(o.ativo === 1 || o.ativo === true);
        if (!window.confirm(`${novo ? 'Ativar' : 'Bloquear'} ${o.nome_fantasia}?`)) return;
        try { await api.put('/api/oficinas/status', { ids: [o.id], ativo: novo }); toast.success(`Parceiro ${novo ? 'ativado' : 'bloqueado'}.`); fetchData(); }
        catch { toast.error('Erro ao alterar status.'); }
    };

    const handleMassStatus = async (novo) => {
        if (!selectedOficinas.length) return toast.warning('Selecione pelo menos um parceiro.');
        if (!window.confirm(`${novo ? 'ATIVAR' : 'BLOQUEAR'} ${selectedOficinas.length} parceiros?`)) return;
        try { await api.put('/api/oficinas/status', { ids: selectedOficinas, ativo: novo }); toast.success(`${selectedOficinas.length} parceiros atualizados.`); fetchData(); setSelectedOficinas([]); }
        catch { toast.error('Erro ao alterar status em massa.'); }
    };

    const handleDeleteMass = async (all) => {
        if (!all && !selectedOficinas.length) return toast.warning('Selecione pelo menos um parceiro.');
        if (!all && !window.confirm(`Apagar ${selectedOficinas.length} parceiros?`)) return;
        try {
            const r = await api.post('/api/oficinas/excluir-massa', { ids: all ? [] : selectedOficinas, deleteAll: all });
            toast.success(r.data.message); fetchData(); setSelectedOficinas([]); setModalConfig({ type: null }); setConfirmDeleteText('');
        } catch (err) { toast.error(err.response?.data?.error || 'Erro ao excluir.'); }
    };

    const toggleSelectOficina = (id) => setSelectedOficinas(p => p.includes(id) ? p.filter(i => i !== id) : [...p, id]);

    const listaFiltrada = useMemo(() => {
        if (activeTab === 'manutencoes') return manutencoes;
        const t = filtro.toLowerCase();
        return oficinas.filter(o => o.nome_fantasia?.toLowerCase().includes(t) || o.cnpj?.includes(t));
    }, [activeTab, manutencoes, oficinas, filtro]);

    const toggleSelectAll = () => {
        const ids = listaFiltrada.map(o => o.id);
        setSelectedOficinas(ids.every(id => selectedOficinas.includes(id)) ? [] : ids);
    };

    const kpis = useMemo(() => ({
        abertos:     statsManut.abertos,
        pendentes:   statsManut.pendentes,
        parceiros:   oficinas.length,
        totalUnread: Object.values(unreadByManutencao).reduce((a, b) => a + b, 0),
    }), [statsManut, oficinas, unreadByManutencao]);

    const openEditManutencao = (item) => {
        setFormManutencao({ veiculo_id: item.veiculo_placa, tipo_servico: item.tipo_servico, descricao_problema: item.descricao_problema });
        setFormManutencaoOficinas(new Set()); setFormManutencaoCanal('email');
        setModalConfig({ type: 'nova', data: item });
    };
    const openEditFornecedor = (item) => {
        const tels = extrairTelefones(item);
        setTelefonesForm(tels.length ? tels : ['']); setTelPrioritario(0);
        setFormFornecedor({ ...item }); setModalConfig({ type: 'fornecedor', data: item });
    };
    const openNovoFornecedor = () => {
        setFormFornecedor({nome_fantasia:'',razao_social:'',cnpj:'',email:'',telefone:'',especialidade:'',cep:'',logradouro:'',numero:'',complemento:'',bairro:'',cidade:'',estado:''});
        setTelefonesForm(['']); setTelPrioritario(0);
        setModalConfig({ type: 'fornecedor', data: null });
    };

    const temFiltrosAtivos = Object.values(filtrosAvancados).some(v => v !== '');

    return (
        <div className="go-main-layout">

            {/* Header */}
            <div className="go-page-header">
                <div>
                    <h1 className="go-page-title">
                        Gestão de Oficinas
                        {kpis.totalUnread > 0 && (
                            <span style={{ marginLeft: 12, background: '#ef4444', color: '#fff', fontSize: '0.75rem', fontWeight: 700, padding: '3px 10px', borderRadius: 20, verticalAlign: 'middle', animation: 'go-badge-pulse 2s infinite', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                <FaBell size={10} /> {kpis.totalUnread} nova{kpis.totalUnread > 1 ? 's' : ''} resposta{kpis.totalUnread > 1 ? 's' : ''}
                            </span>
                        )}
                    </h1>
                    <p className="go-page-subtitle">Central de Manutenção e Rede Credenciada</p>
                </div>
                <div className="go-tabs-pill">
                    <button className={activeTab === 'manutencoes' ? 'active' : ''} onClick={() => setActiveTab('manutencoes')}>Manutenções</button>
                    <button className={activeTab === 'fornecedores' ? 'active' : ''} onClick={() => setActiveTab('fornecedores')}>Rede Credenciada</button>
                </div>
            </div>

            {/* KPIs */}
            <div className="go-kpi-row">
                <div className="go-kpi-card-new color-blue"><div className="kpi-icon"><FaWrench/></div><div className="kpi-content"><span>Em Aberto</span><strong>{kpis.abertos}</strong></div></div>
                <div className="go-kpi-card-new color-orange"><div className="kpi-icon"><FaExclamationTriangle/></div><div className="kpi-content"><span>Pendentes</span><strong>{kpis.pendentes}</strong></div></div>
                <div className="go-kpi-card-new color-green"><div className="kpi-icon"><FaBuilding/></div><div className="kpi-content"><span>Parceiros (Pág)</span><strong>{kpis.parceiros}</strong></div></div>
                {kpis.totalUnread > 0 && (
                    <div className="go-kpi-card-new" style={{ borderColor: '#fca5a5', background: '#fff5f5' }}>
                        <div className="kpi-icon" style={{ background: '#fee2e2', color: '#ef4444', animation: 'go-badge-pulse 2s infinite' }}><FaBell/></div>
                        <div className="kpi-content"><span style={{ color: '#ef4444' }}>Respostas Novas</span><strong style={{ color: '#ef4444' }}>{kpis.totalUnread}</strong></div>
                    </div>
                )}
            </div>

            {/* Conteúdo */}
            <div className="go-content-card">

                {/* Toolbar */}
                <div className="go-toolbar-new">
                    <div className="go-search-box">
                        <FaSearch />
                        <input type="text" placeholder={activeTab === 'manutencoes' ? 'Busca rápida...' : 'Buscar parceiro...'} value={filtro} onChange={e => setFiltro(e.target.value)} />
                        {filtro && <button className="go-search-clear" onClick={() => setFiltro('')}><FaTimes /></button>}
                    </div>

                    {/* SmartFiltroBar — só na aba de manutenções */}
                    {activeTab === 'manutencoes' && (
                        <SmartFiltroBar
                            filtros={filtrosAvancados}
                            onFiltrosChange={(f) => { setFiltrosAvancados(f); setPageManut(1); }}
                            onLimpar={limparFiltros}
                            iaQuery={iaQuery}
                            onIAQueryChange={setIaQuery}
                            onInterpretarIA={handleInterpretarIA}
                            loadingIA={loadingIA}
                            resumoIA={iaResumo}
                            erroIA={iaErro}
                        />
                    )}

                    <div className="go-actions">
                        {activeTab === 'manutencoes' ? (
                            <>
                                <AcoesMenu>
                                    <button type="button" className="go-acoes-item" onClick={abrirImportManutencao}><FaUpload /> Importar Manutenções</button>
                                    <button type="button" className="go-acoes-item" onClick={handleDownloadModeloManutencao}><FaFileExcel /> Baixar Modelo</button>
                                    <button type="button" className="go-acoes-item" onClick={() => window.open(`${api.defaults.baseURL}/api/frota/manutencoes/exportar`)}><FaDownload /> Exportar Manutenções</button>
                                </AcoesMenu>
                                <button className="go-btn-new primary" onClick={() => { setFormManutencao({veiculo_id:'',tipo_servico:'',descricao_problema:''}); setFormManutencaoOficinas(new Set()); setFormManutencaoCanal('email'); setModalConfig({type:'nova',data:null}); }}>
                                    <FaPlus /> Nova Solicitação
                                </button>
                            </>
                        ) : (
                            <>
                                {selectedOficinas.length > 0 && (
                                    <div className="mass-actions-group">
                                        <button className="go-btn-new danger sm outline" onClick={() => handleDeleteMass(false)}><FaTrash /></button>
                                        <button className="go-btn-new danger sm" onClick={() => handleMassStatus(false)}><FaBan /> Bloquear ({selectedOficinas.length})</button>
                                        <button className="go-btn-new success sm" onClick={() => handleMassStatus(true)}><FaCheckCircle /> Ativar ({selectedOficinas.length})</button>
                                    </div>
                                )}
                                <AcoesMenu>
                                    <button type="button" className="go-acoes-item" onClick={() => setModalConfig({type:'importacao',data:null})}><FaUpload /> Importar Parceiros</button>
                                    <button type="button" className="go-acoes-item" onClick={handleDownloadTemplate}><FaFileExcel /> Baixar Modelo</button>
                                    <button type="button" className="go-acoes-item" onClick={() => window.open(`${api.defaults.baseURL}/api/oficinas/exportar`)}><FaDownload /> Exportar Parceiros</button>
                                    <button type="button" className="go-acoes-item danger" onClick={() => { setConfirmDeleteText(''); setModalConfig({type:'delete_all'}); }}><FaTrash /> Zerar Base</button>
                                </AcoesMenu>
                                <button className="go-btn-new primary" onClick={openNovoFornecedor}><FaPlus /> Novo Parceiro</button>
                            </>
                        )}
                    </div>
                </div>

                {/* Banner de filtros ativos */}
                {activeTab === 'manutencoes' && temFiltrosAtivos && (
                    <div className="go-filtros-active-bar">
                        <FaFilter /> <span>Filtros ativos — resultados filtrados</span>
                        <button className="go-filtros-clear-link" onClick={limparFiltros}><FaTimes /> Limpar</button>
                    </div>
                )}

                {/* Tabela */}
                {(activeTab === 'manutencoes' ? loadingManut : loadingOf) ? (
                    <div className="go-loading"><FaSpinner className="go-spin-icon"/> Carregando dados...</div>
                ) : (
                    <div className="go-table-responsive">
                        <table className="go-table-modern">
                            <thead>
                                {activeTab === 'manutencoes' ? (
                                    <tr>
                                        <th style={{width:'5%'}}>ID</th>
                                        <th style={{width:'20%'}}>Veículo</th>
                                        <th style={{width:'28%'}}>Oficina / Status</th>
                                        <th style={{width:'18%'}}>Serviço</th>
                                        <th style={{width:'10%'}}>Custo</th>
                                        <th style={{width:'19%',textAlign:'right'}}>Ações</th>
                                    </tr>
                                ) : (
                                    <tr>
                                        <th style={{width:'5%',textAlign:'center'}}><div onClick={toggleSelectAll} style={{cursor:'pointer'}}>{listaFiltrada.length > 0 && listaFiltrada.every(o => selectedOficinas.includes(o.id)) ? <FaCheckSquare className="txt-highlight" /> : <FaSquare style={{color:'#cbd5e1'}} />}</div></th>
                                        <th style={{width:'30%'}}>Parceiro</th>
                                        <th style={{width:'25%'}}>Contato</th>
                                        <th style={{width:'20%'}}>Especialidade</th>
                                        <th style={{width:'10%'}}>Status</th>
                                        <th style={{width:'10%',textAlign:'right'}}>Ações</th>
                                    </tr>
                                )}
                            </thead>
                            <tbody>
                                {activeTab === 'manutencoes'
                                    ? listaFiltrada.map(item => {
                                        const unread = unreadByManutencao[item.id] || 0;
                                        return (
                                            <tr key={item.id} className={unread > 0 ? 'row-has-unread' : ''}>
                                                <td style={{fontWeight:'bold',color:'#64748b'}}>#{item.id}</td>
                                                <td><div className="d-flex-col"><span className="txt-highlight">{item.veiculo_placa}</span><span className="txt-sub">{item.veiculo_modelo}</span></div></td>
                                                <td><div className="office-status-cell"><span className="office-name">{item.oficina_nome || '---'}</span><StatusBadge status={item.status} /></div></td>
                                                <td>{item.tipo_servico}</td>
                                                <td style={{fontWeight:600}}>{formatCurrency(item.valor_final)}</td>
                                                <td align="right">
                                                    <div className="actions-cell">
                                                        <button className="go-btn-new ghost sm" onClick={() => openEditManutencao(item)}><FaEdit/></button>
                                                        <button className={`go-btn-new sm ${unread > 0 ? 'btn-gerenciar-unread' : 'outline'}`} onClick={() => setModalConfig({type:'super_modal',data:item})} style={{position:'relative'}}>
                                                            <FaCog /> Gerenciar
                                                            {unread > 0 && <span style={{position:'absolute',top:-6,right:-6,background:'#ef4444',color:'#fff',borderRadius:'50%',width:18,height:18,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'0.65rem',fontWeight:800,animation:'go-badge-pulse 1.5s infinite',border:'2px solid white'}}>{unread > 9 ? '9+' : unread}</span>}
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                    : listaFiltrada.map(item => {
                                        const tels = extrairTelefones(item);
                                        const extras = Math.max(0, tels.length - 1);
                                        return (
                                            <tr key={item.id} className={selectedOficinas.includes(item.id) ? 'row-selected' : ''}>
                                                <td align="center"><div onClick={() => toggleSelectOficina(item.id)} style={{cursor:'pointer'}}>{selectedOficinas.includes(item.id) ? <FaCheckSquare className="txt-highlight" /> : <FaSquare style={{color:'#cbd5e1'}} />}</div></td>
                                                <td><div className="company-cell"><CompanyAvatar name={item.nome_fantasia}/><div className="d-flex-col"><span className="txt-highlight">{item.nome_fantasia}</span><span className="txt-sub">{item.cnpj}</span></div></div></td>
                                                <td><div className="txt-sub">{item.email}<br/>{tels[0] || '—'}{extras > 0 && <span className="tel-extra-badge" title={`+${extras}: ${tels.slice(1).join(', ')}`}>+{extras}</span>}</div></td>
                                                <td>{item.especialidade}</td>
                                                <td><StatusBadge status={item.ativo} /></td>
                                                <td align="right"><div className="actions-cell"><button className={`go-btn-new sm ${item.ativo ? 'ghost-danger' : 'ghost-success'}`} onClick={() => handleToggleStatus(item)}>{item.ativo ? <FaLock /> : <FaCheck />}</button><button className="go-btn-new ghost sm" onClick={() => openEditFornecedor(item)}><FaEdit/></button></div></td>
                                            </tr>
                                        );
                                    })
                                }
                            </tbody>
                        </table>
                        {listaFiltrada.length === 0 && (
                            <div className="go-empty-list">
                                {temFiltrosAtivos ? <><FaFilter style={{marginRight:6}}/> Nenhuma manutenção com esses filtros. <button onClick={limparFiltros} className="go-filtros-clear-link" style={{marginLeft:8}}>Limpar filtros</button></> : 'Nenhum registro encontrado.'}
                            </div>
                        )}
                    </div>
                )}

                {/* Paginação */}
                {activeTab === 'manutencoes' && (
                    <div className="go-pagination">
                        <button className="go-btn-new outline sm" disabled={pageManut === 1} onClick={() => setPageManut(p => p-1)}><FaChevronLeft /> Anterior</button>
                        <span>Página <strong>{pageManut}</strong> de <strong>{totalPagesManut}</strong></span>
                        <button className="go-btn-new outline sm" disabled={pageManut >= totalPagesManut} onClick={() => setPageManut(p => p+1)}>Próxima <FaChevronRight /></button>
                    </div>
                )}
                {activeTab === 'fornecedores' && (
                    <div className="go-pagination">
                        <button className="go-btn-new outline sm" disabled={page === 1} onClick={() => setPage(p => p-1)}><FaChevronLeft /> Anterior</button>
                        <span>Página <strong>{page}</strong> de <strong>{totalPages}</strong></span>
                        <button className="go-btn-new outline sm" disabled={page >= totalPages} onClick={() => setPage(p => p+1)}>Próxima <FaChevronRight /></button>
                    </div>
                )}
            </div>

            {/* ══ CSS COMPLETO ══ */}
            <style>{`
                @keyframes go-badge-pulse { 0%,100%{transform:scale(1)} 50%{transform:scale(1.1)} }
                @keyframes sfb-fade-in { from{opacity:0;transform:translateY(-6px)} to{opacity:1;transform:none} }
                @keyframes sfb-spin { to{transform:rotate(360deg)} }

                /* Unread */
                .row-has-unread td { background-color:#fff9f9!important; }
                .row-has-unread td:first-child { border-left:3px solid #ef4444; }
                .btn-gerenciar-unread { background:#fef2f2!important; border-color:#fca5a5!important; color:#b91c1c!important; font-weight:700; }
                .btn-gerenciar-unread:hover { background:#fee2e2!important; }

                /* Search clear btn */
                .go-search-box { position:relative; }
                .go-search-clear { position:absolute; right:10px; top:50%; transform:translateY(-50%); background:none; border:none; color:#94a3b8; cursor:pointer; padding:2px; display:flex; align-items:center; }
                .go-search-clear:hover { color:#475569; }

                /* Banner de filtros ativos (abaixo da toolbar) */
                .go-filtros-active-bar {
                    display:flex; align-items:center; gap:8px;
                    padding:8px 16px; margin:0 0 2px;
                    background:#f0f9ff; border-bottom:1px solid #bae6fd;
                    font-size:0.8rem; color:#0369a1; font-weight:600;
                }
                .go-filtros-clear-link {
                    display:inline-flex; align-items:center; gap:4px;
                    margin-left:auto; background:none; border:none;
                    color:#0369a1; font-size:0.78rem; font-weight:700;
                    cursor:pointer; padding:2px 6px; border-radius:4px;
                    font-family:inherit;
                }
                .go-filtros-clear-link:hover { background:#e0f2fe; }

                /* ══ SMART FILTRO BAR ══ */
                .sfb-root { flex:1; min-width:0; display:flex; flex-direction:column; gap:6px; }

                .sfb-bar { display:flex; align-items:center; gap:8px; }

                /* Campo de IA */
                .sfb-ia-wrap {
                    flex:1; min-width:0; display:flex; align-items:center; gap:8px;
                    height:38px; padding:0 10px;
                    background:#fff;
                    border:1.5px solid #e2e8f0;
                    border-radius:10px;
                    transition:border-color .2s, box-shadow .2s;
                }
                .sfb-ia-wrap:focus-within {
                    border-color:#8b5cf6;
                    box-shadow:0 0 0 3px rgba(139,92,246,.1);
                }
                .sfb-ia-wrap.sfb-ia-loading {
                    border-color:#8b5cf6;
                    background:linear-gradient(90deg,#fff 0%,#f5f3ff 50%,#fff 100%);
                    background-size:200% 100%;
                    animation:sfb-shimmer 1.4s infinite;
                }
                @keyframes sfb-shimmer { from{background-position:-200% 0} to{background-position:200% 0} }
                .sfb-ia-wrap.sfb-ia-ok  { border-color:#8b5cf6; background:#faf5ff; }
                .sfb-ia-wrap.sfb-ia-erro { border-color:#fca5a5; background:#fff9f9; }

                .sfb-ia-glyph { color:#8b5cf6; font-size:0.88rem; flex-shrink:0; }
                .sfb-ia-glyph.sfb-spin  { animation:sfb-spin .8s linear infinite; }
                .sfb-ia-input {
                    flex:1; border:none; background:transparent; outline:none;
                    font-size:0.85rem; color:#334155; min-width:0;
                }
                .sfb-ia-input::placeholder { color:#94a3b8; }
                .sfb-ia-send {
                    display:inline-flex; align-items:center; gap:5px;
                    padding:4px 10px; border-radius:7px;
                    background:#8b5cf6; color:#fff; border:none;
                    font-size:0.78rem; font-weight:700; cursor:pointer;
                    white-space:nowrap; font-family:inherit;
                    transition:background .15s;
                    flex-shrink:0;
                }
                .sfb-ia-send:hover { background:#7c3aed; }
                .sfb-ia-loading-txt { font-size:0.75rem; color:#8b5cf6; font-weight:600; white-space:nowrap; flex-shrink:0; }
                .sfb-ia-clear-btn { background:none; border:none; color:#94a3b8; cursor:pointer; padding:2px; display:flex; align-items:center; flex-shrink:0; font-size:0.9rem; }
                .sfb-ia-clear-btn:hover { color:#475569; }

                /* Botão Filtros */
                .sfb-filtros-wrap { position:relative; flex-shrink:0; }
                .sfb-filtros-btn {
                    display:inline-flex; align-items:center; gap:6px;
                    height:38px; padding:0 14px;
                    border:1.5px solid #e2e8f0; border-radius:10px;
                    background:#fff; color:#64748b;
                    font-size:0.84rem; font-weight:600; cursor:pointer;
                    font-family:inherit; transition:all .15s; white-space:nowrap;
                }
                .sfb-filtros-btn:hover { border-color:#94a3b8; color:#334155; }
                .sfb-filtros-btn--ativo { border-color:#3b82f6; color:#2563eb; background:#eff6ff; }
                .sfb-filtros-btn--open  { background:#f1f5f9; border-color:#94a3b8; color:#0f172a; }
                .sfb-qtd-badge { background:#3b82f6; color:#fff; border-radius:20px; padding:1px 7px; font-size:0.7rem; font-weight:800; }
                .sfb-filtros-btn--ativo .sfb-qtd-badge { background:#2563eb; }
                .sfb-caret { font-size:0.7rem; color:#94a3b8; transition:transform .2s; }
                .sfb-caret--up { transform:rotate(180deg); }

                /* Painel de filtros */
                .sfb-panel {
                    position:absolute; top:calc(100% + 8px); right:0; z-index:70;
                    background:#fff; border:1px solid #e2e8f0; border-radius:14px;
                    box-shadow:0 20px 50px rgba(15,23,42,.14);
                    width:520px; max-width:calc(100vw - 24px);
                    animation:sfb-fade-in .15s ease;
                }
                .sfb-panel-head {
                    display:flex; align-items:center; justify-content:space-between;
                    padding:14px 18px 10px;
                    border-bottom:1px solid #f1f5f9;
                }
                .sfb-panel-title { font-size:0.88rem; font-weight:700; color:#0f172a; display:flex; align-items:center; gap:7px; }
                .sfb-panel-title svg { color:#64748b; font-size:0.82rem; }
                .sfb-panel-limpar {
                    display:inline-flex; align-items:center; gap:5px;
                    background:none; border:none; color:#ef4444; font-size:0.78rem;
                    font-weight:700; cursor:pointer; padding:4px 8px; border-radius:6px;
                    font-family:inherit; transition:background .15s;
                }
                .sfb-panel-limpar:hover { background:#fef2f2; }

                .sfb-panel-grid {
                    display:grid; grid-template-columns:1fr 1fr; gap:12px;
                    padding:14px 18px;
                }
                .sfb-field { display:flex; flex-direction:column; gap:4px; }
                .sfb-label { font-size:0.72rem; font-weight:700; color:#64748b; text-transform:uppercase; letter-spacing:.4px; display:flex; align-items:center; gap:5px; }
                .sfb-label svg { font-size:0.7rem; color:#94a3b8; }
                .sfb-input, .sfb-select {
                    height:36px; padding:0 10px;
                    border:1.5px solid #e2e8f0; border-radius:8px;
                    background:#f8fafc; font-size:0.84rem; color:#334155;
                    outline:none; width:100%; font-family:inherit;
                    transition:border-color .15s, background .15s;
                }
                .sfb-input:focus, .sfb-select:focus { border-color:#3b82f6; background:#fff; }
                .sfb-select { appearance:none; cursor:pointer; }

                .sfb-panel-foot {
                    padding:12px 18px 14px;
                    border-top:1px solid #f1f5f9;
                    display:flex; justify-content:flex-end;
                }
                .sfb-panel-apply {
                    display:inline-flex; align-items:center; gap:7px;
                    padding:9px 20px; border-radius:9px;
                    background:#2563eb; color:#fff; border:none;
                    font-size:0.86rem; font-weight:700; cursor:pointer;
                    font-family:inherit; transition:background .15s;
                }
                .sfb-panel-apply:hover { background:#1d4ed8; }

                /* Chips */
                .sfb-chips-row {
                    display:flex; flex-wrap:wrap; align-items:center; gap:6px;
                    padding:4px 0;
                }
                .sfb-chips-label { font-size:0.73rem; font-weight:700; color:#94a3b8; text-transform:uppercase; letter-spacing:.4px; white-space:nowrap; }
                .sfb-chip {
                    display:inline-flex; align-items:center; gap:5px;
                    padding:3px 8px 3px 10px; border-radius:20px;
                    font-size:0.75rem; font-weight:500;
                }
                .sfb-chip-txt strong { font-weight:700; }
                .sfb-chip-x {
                    background:none; border:none; cursor:pointer; padding:1px;
                    display:inline-flex; align-items:center; font-size:0.65rem;
                    color:inherit; opacity:.7; border-radius:50%;
                    transition:opacity .15s;
                }
                .sfb-chip-x:hover { opacity:1; }
                .sfb-clear-all {
                    display:inline-flex; align-items:center; gap:4px;
                    background:none; border:none; color:#64748b; font-size:0.74rem;
                    font-weight:600; cursor:pointer; padding:2px 6px;
                    border-radius:6px; font-family:inherit; transition:all .15s;
                }
                .sfb-clear-all:hover { background:#f1f5f9; color:#0f172a; }

                /* Banner IA */
                .sfb-banner {
                    display:flex; align-items:center; gap:8px;
                    padding:7px 12px; border-radius:8px;
                    font-size:0.78rem; font-weight:500;
                }
                .sfb-banner--ok  { background:#f5f3ff; border:1px solid #ddd6fe; color:#5b21b6; }
                .sfb-banner--ok svg { color:#8b5cf6; }
                .sfb-banner--erro { background:#fef2f2; border:1px solid #fecaca; color:#991b1b; }
                .sfb-banner--erro svg { color:#ef4444; }

                /* Menus e modais existentes */
                .go-acoes-menu { position:relative; display:inline-block; }
                .go-btn-new.go-acoes-open { background:#f1f5f9; border-color:#94a3b8; color:#1e293b; }
                .go-acoes-dropdown { position:absolute; top:calc(100% + 6px); right:0; z-index:60; background:#fff; border:1px solid #e2e8f0; border-radius:10px; box-shadow:0 14px 34px rgba(15,23,42,.16); padding:6px; min-width:234px; display:flex; flex-direction:column; gap:2px; animation:sfb-fade-in .14s ease; }
                .go-acoes-item { display:flex; align-items:center; gap:10px; width:100%; padding:10px 12px; border:none; background:transparent; border-radius:7px; font-size:0.88rem; font-weight:600; color:#334155; cursor:pointer; font-family:inherit; text-align:left; white-space:nowrap; transition:background .15s,color .15s; }
                .go-acoes-item:hover { background:#f1f5f9; color:#0f172a; }
                .go-acoes-item svg { color:#64748b; font-size:0.95rem; flex-shrink:0; }
                .go-acoes-item.danger { color:#b91c1c; }
                .go-acoes-item.danger svg { color:#ef4444; }
                .go-acoes-item.danger:hover { background:#fef2f2; }

                /* Import result */
                .go-import-result { margin-top:16px; display:flex; flex-direction:column; gap:10px; }
                .go-import-stats { display:grid; grid-template-columns:repeat(auto-fit,minmax(76px,1fr)); gap:10px; }
                .go-import-stat { background:#f8fafc; border:1px solid #e2e8f0; border-radius:8px; padding:12px; text-align:center; }
                .go-import-stat strong { display:block; font-size:1.5rem; font-weight:800; color:#0f172a; line-height:1.1; }
                .go-import-stat span { font-size:0.68rem; color:#64748b; text-transform:uppercase; letter-spacing:.4px; font-weight:700; }
                .go-import-stat.ok strong { color:#16a34a; }
                .go-import-stat.warn strong { color:#d97706; }
                .go-import-stat.muted strong { color:#64748b; }
                .go-import-naoenc { background:#fffbeb; border:1px solid #fde68a; border-radius:8px; padding:11px 13px; font-size:0.8rem; color:#78350f; }
                .go-import-naoenc .placas { margin-top:8px; display:flex; flex-wrap:wrap; gap:5px; }
                .go-import-naoenc .placa-chip { background:#fff; border:1px solid #fcd34d; border-radius:6px; padding:2px 8px; font-size:0.74rem; font-weight:700; color:#92400e; }
                .go-import-erros { background:#fef2f2; border:1px solid #fecaca; border-radius:8px; padding:11px 13px; font-size:0.8rem; color:#7f1d1d; }
                .go-import-erros ul { margin:8px 0 0; padding-left:18px; }
                .go-import-erros li { margin-bottom:3px; word-break:break-word; }

                /* Canal switch */
                .canal-switch { display:inline-flex; gap:6px; padding:5px; background:#f1f5f9; border-radius:12px; border:1px solid #e2e8f0; }
                .canal-switch-pill { display:inline-flex; align-items:center; gap:8px; padding:9px 18px; border-radius:9px; font-size:0.88rem; font-weight:700; cursor:pointer; border:none; background:transparent; color:#64748b; transition:all .18s; font-family:inherit; }
                .canal-switch-pill.active.email  { background:#fff; color:#2563eb; box-shadow:0 1px 4px rgba(15,23,42,.12); }
                .canal-switch-pill.active.whatsapp { background:#fff; color:#16a34a; box-shadow:0 1px 4px rgba(15,23,42,.12); }

                .of-canal-warn { display:flex; align-items:center; gap:8px; padding:9px 13px; background:#fffbeb; border:1px solid #fde68a; border-radius:8px; font-size:0.8rem; color:#78350f; }
                .of-canal-warn svg { color:#d97706; flex-shrink:0; }
                .of-selector { display:flex; flex-direction:column; gap:10px; }
                .of-mode-pills { display:flex; gap:8px; flex-wrap:wrap; }
                .of-mode-pill { display:inline-flex; align-items:center; gap:7px; padding:8px 16px; border-radius:8px; font-size:0.85rem; font-weight:600; cursor:pointer; border:1.5px solid #e2e8f0; background:#f8fafc; color:#64748b; transition:all .18s; font-family:inherit; }
                .of-mode-pill:hover { border-color:#94a3b8; color:#334155; }
                .of-mode-pill.active { background:#eff6ff; border-color:#2563eb; color:#2563eb; }
                .of-mode-pill.active.danger { background:#fef2f2; border-color:#ef4444; color:#b91c1c; }
                .of-list-wrapper { border:1.5px solid #e2e8f0; border-radius:10px; overflow:hidden; background:#fff; }
                .of-list-header { display:flex; align-items:center; gap:10px; padding:10px 14px; border-bottom:1px solid #f1f5f9; background:#f8fafc; }
                .of-search { flex:1; display:flex; align-items:center; gap:8px; background:#fff; border:1px solid #e2e8f0; border-radius:6px; padding:0 12px; }
                .of-search svg { color:#94a3b8; font-size:12px; }
                .of-search input { border:none; background:transparent; outline:none; font-size:0.85rem; color:#334155; padding:7px 0; width:100%; }
                .of-select-all-btn { display:inline-flex; align-items:center; gap:6px; background:none; border:none; font-size:0.8rem; font-weight:600; color:#64748b; cursor:pointer; padding:4px 8px; border-radius:6px; font-family:inherit; }
                .of-select-all-btn:hover { background:#f1f5f9; }
                .of-list { max-height:200px; overflow-y:auto; }
                .of-item { display:flex; align-items:center; gap:10px; padding:9px 14px; cursor:pointer; border-bottom:1px solid #f8fafc; transition:background .15s; }
                .of-item:hover { background:#f8fafc; }
                .of-item--selected { background:#eff6ff; }
                .of-item--selected:hover { background:#dbeafe; }
                .of-item--disabled { opacity:.55; cursor:not-allowed; }
                .of-checkbox { width:18px; height:18px; border-radius:5px; border:2px solid #cbd5e1; display:flex; align-items:center; justify-content:center; flex-shrink:0; font-size:9px; color:transparent; transition:all .15s; }
                .of-checkbox--checked { background:#2563eb; border-color:#2563eb; color:#fff; }
                .of-item-info { flex:1; display:flex; flex-direction:column; min-width:0; }
                .of-item-name { font-size:0.88rem; font-weight:600; color:#334155; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
                .of-item-esp { font-size:0.75rem; color:#94a3b8; }
                .of-item-contato { display:inline-flex; align-items:center; gap:5px; font-size:0.72rem; color:#64748b; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width:180px; }
                .of-item-sem-contato { display:inline-flex; align-items:center; gap:5px; font-size:0.7rem; font-weight:600; color:#b45309; background:#fffbeb; border:1px solid #fde68a; border-radius:6px; padding:2px 8px; white-space:nowrap; }
                .of-empty { padding:20px; text-align:center; color:#94a3b8; font-size:0.85rem; }
                .of-summary { display:flex; align-items:center; gap:8px; padding:10px 14px; background:#eff6ff; border:1px solid #bfdbfe; border-radius:8px; font-size:0.82rem; color:#1e40af; }
                .of-summary--wa { background:#f0fdf4; border-color:#bbf7d0; color:#166534; }
                .of-summary strong { font-weight:700; }
            `}</style>

            {/* MODAL COCKPIT */}
            {modalConfig.type === 'super_modal' && (
                <MaintenanceActionModal ticket={modalConfig.data} onClose={handleCloseModal} onUpdate={fetchData} allOficinas={listaCompletaOficinas} />
            )}

            {/* MODAL: Nova / Editar Manutenção */}
            {modalConfig.type === 'nova' && (
                <div className="go-modal-backdrop">
                    <div className="go-modal-window" style={{ width: 680 }}>
                        <div className="go-modal-header">
                            <h3>{modalConfig.data ? 'Editar Manutenção' : 'Nova Solicitação'}</h3>
                            <button className="go-close-btn" onClick={() => setModalConfig({ type: null })}><FaTimesCircle/></button>
                        </div>
                        <div className="go-modal-body">
                            <form id="form-nova" onSubmit={submitManutencao} className="go-grid-form">
                                <div className="go-form-group">
                                    <label>Placa do Veículo</label>
                                    <input required value={formManutencao.veiculo_id} onChange={e => setFormManutencao({...formManutencao,veiculo_id:e.target.value})} placeholder="ABC-1234" disabled={!!modalConfig.data}/>
                                </div>
                                <div className="go-form-group">
                                    <label>Tipo de Serviço</label>
                                    <input required value={formManutencao.tipo_servico} onChange={e => setFormManutencao({...formManutencao,tipo_servico:e.target.value})} placeholder="Ex: Troca de óleo"/>
                                </div>
                                <div className="go-form-group full-width">
                                    <label>Descrição do Problema</label>
                                    <textarea rows={3} value={formManutencao.descricao_problema} onChange={e => setFormManutencao({...formManutencao,descricao_problema:e.target.value})} placeholder="Descreva o problema ou serviço necessário..."/>
                                </div>
                                {!modalConfig.data && (
                                    <>
                                        <div className="go-form-group full-width">
                                            <label>Meio de Contato</label>
                                            <CanalSwitch canal={formManutencaoCanal} onChange={setFormManutencaoCanal} />
                                            <small style={{color:'#94a3b8',marginTop:6,display:'block'}}>{formManutencaoCanal==='whatsapp' ? 'A cotação será enviada por WhatsApp ao telefone prioritário.' : 'A cotação será enviada por e-mail, com possibilidade de anexos.'}</small>
                                        </div>
                                        <div className="go-form-group full-width">
                                            <label>Encaminhar para Oficinas</label>
                                            <OficinaSelector oficinas={listaCompletaOficinas} selected={formManutencaoOficinas} onChange={setFormManutencaoOficinas} canal={formManutencaoCanal} />
                                        </div>
                                    </>
                                )}
                            </form>
                        </div>
                        <div className="go-modal-footer">
                            <button className="go-btn-new ghost" onClick={() => setModalConfig({ type: null })} disabled={savingManutencao}>Cancelar</button>
                            <button type="submit" form="form-nova" className="go-btn-new primary" disabled={savingManutencao}>
                                {savingManutencao ? <><FaSpinner className="go-spin-icon"/> Salvando...</> : formManutencaoOficinas.size > 0 ? <>{formManutencaoCanal==='whatsapp' ? <FaWhatsapp/> : <FaPaperPlane/>} Salvar e Disparar ({formManutencaoOficinas.size})</> : 'Salvar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL: Novo / Editar Parceiro */}
            {modalConfig.type === 'fornecedor' && (
                <div className="go-modal-backdrop">
                    <div className="go-modal-window wide">
                        <div className="go-modal-header">
                            <h3>{modalConfig.data ? 'Editar Parceiro' : 'Novo Parceiro'}</h3>
                            <button className="go-close-btn" onClick={() => setModalConfig({ type: null })}><FaTimesCircle/></button>
                        </div>
                        <div className="go-modal-body">
                            <form id="form-forn" onSubmit={submitFornecedor} className="go-grid-form">
                                <div className="go-form-group"><label>Nome Fantasia</label><input required value={formFornecedor.nome_fantasia||''} onChange={e => setFormFornecedor({...formFornecedor,nome_fantasia:e.target.value})}/></div>
                                <div className="go-form-group"><label>Razão Social</label><input value={formFornecedor.razao_social||''} onChange={e => setFormFornecedor({...formFornecedor,razao_social:e.target.value})}/></div>
                                <div className="go-form-group"><label>Email</label><input required type="email" value={formFornecedor.email||''} onChange={e => setFormFornecedor({...formFornecedor,email:e.target.value})}/></div>
                                <div className="go-form-group"><label>CNPJ</label><input required value={formFornecedor.cnpj||''} onChange={e => setFormFornecedor({...formFornecedor,cnpj:e.target.value})}/></div>
                                <div className="go-form-group"><label>Especialidade</label><input value={formFornecedor.especialidade||''} onChange={e => setFormFornecedor({...formFornecedor,especialidade:e.target.value})}/></div>
                                <div className="go-form-group full-width">
                                    <label style={{display:'flex',alignItems:'center',gap:6}}><FaPhone size={11}/> Telefones (até {MAX_TELEFONES}) <span style={{fontWeight:400,color:'#94a3b8',fontSize:'0.8rem'}}>— ★ define o prioritário (WhatsApp)</span></label>
                                    <div className="tel-manager">
                                        {telefonesForm.map((tel, i) => (
                                            <div className={`tel-row ${telPrioritario === i ? 'tel-row--priority' : ''}`} key={i}>
                                                <button type="button" className="tel-star" onClick={() => setTelPrioritario(i)}>{telPrioritario === i ? <FaStar/> : <FaRegStar/>}</button>
                                                <input value={tel} onChange={e => updateTelefone(i, e.target.value)} placeholder={i === 0 ? 'Telefone principal' : `Telefone ${i+1}`}/>
                                                {telPrioritario === i && <span className="tel-priority-tag">Prioritário</span>}
                                                {telefonesForm.length > 1 && <button type="button" className="tel-remove" onClick={() => removeTelefone(i)}><FaTimes/></button>}
                                            </div>
                                        ))}
                                        {telefonesForm.length < MAX_TELEFONES && <button type="button" className="tel-add" onClick={addTelefone}><FaPlus/> Adicionar telefone</button>}
                                    </div>
                                </div>
                                <div className="go-form-group full-width" style={{marginTop:8,borderBottom:'1px solid #e2e8f0',paddingBottom:4}}><h4 style={{margin:0,color:'#475569'}}>Endereço</h4></div>
                                <div className="go-form-group"><label>CEP</label><input value={formFornecedor.cep||''} placeholder="00000-000" maxLength={9} onChange={e => setFormFornecedor({...formFornecedor,cep:e.target.value})} onBlur={e => buscarCep(e.target.value)}/></div>
                                <div className="go-form-group"><label>Logradouro</label><input value={formFornecedor.logradouro||''} onChange={e => setFormFornecedor({...formFornecedor,logradouro:e.target.value})}/></div>
                                <div className="go-form-group"><label>Número</label><input value={formFornecedor.numero||''} onChange={e => setFormFornecedor({...formFornecedor,numero:e.target.value})}/></div>
                                <div className="go-form-group"><label>Complemento</label><input value={formFornecedor.complemento||''} onChange={e => setFormFornecedor({...formFornecedor,complemento:e.target.value})}/></div>
                                <div className="go-form-group"><label>Bairro</label><input value={formFornecedor.bairro||''} onChange={e => setFormFornecedor({...formFornecedor,bairro:e.target.value})}/></div>
                                <div className="go-form-group" style={{display:'grid',gridTemplateColumns:'2fr 1fr',gap:10}}>
                                    <div><label>Cidade</label><input value={formFornecedor.cidade||''} onChange={e => setFormFornecedor({...formFornecedor,cidade:e.target.value})}/></div>
                                    <div><label>UF</label><input maxLength={2} style={{textTransform:'uppercase'}} value={formFornecedor.estado||''} onChange={e => setFormFornecedor({...formFornecedor,estado:e.target.value.toUpperCase()})}/></div>
                                </div>
                            </form>
                        </div>
                        <div className="go-modal-footer">
                            <button className="go-btn-new ghost" onClick={() => setModalConfig({ type: null })}>Cancelar</button>
                            <button type="submit" form="form-forn" className="go-btn-new primary">Salvar</button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL: Zerar Base */}
            {modalConfig.type === 'delete_all' && (
                <div className="go-modal-backdrop">
                    <div className="go-modal-window">
                        <div className="go-modal-header" style={{borderBottom:'none',paddingBottom:0}}><button className="go-close-btn" onClick={() => setModalConfig({type:null})}><FaTimesCircle/></button></div>
                        <div className="go-modal-body" style={{textAlign:'center',padding:'0 30px 20px'}}>
                            <FaExclamationTriangle size={60} color="#ef4444" style={{marginBottom:15}}/>
                            <h2 style={{color:'#0f172a',marginBottom:10}}>Atenção Extrema!</h2>
                            <p style={{color:'#475569',marginBottom:20,lineHeight:1.5}}>Você está prestes a <strong>APAGAR TODOS OS PARCEIROS</strong> da base. Esta ação é irreversível.<br/><br/><em>Parceiros com histórico vinculado não serão apagados.</em></p>
                            <div style={{background:'#fef2f2',border:'1px solid #fca5a5',padding:15,borderRadius:8,marginBottom:20}}>
                                <label style={{display:'block',marginBottom:10,color:'#991b1b',fontWeight:'bold',fontSize:'0.9rem'}}>Para confirmar, digite APAGAR</label>
                                <input type="text" placeholder="APAGAR" value={confirmDeleteText} onChange={e => setConfirmDeleteText(e.target.value)} style={{width:'100%',padding:10,textAlign:'center',fontWeight:'bold',border:'1px solid #f87171',borderRadius:6,outline:'none'}}/>
                            </div>
                        </div>
                        <div className="go-modal-footer" style={{justifyContent:'center',background:'#f8fafc'}}>
                            <button className="go-btn-new ghost" onClick={() => setModalConfig({type:null})}>Cancelar</button>
                            <button className="go-btn-new danger" disabled={confirmDeleteText !== 'APAGAR'} onClick={() => handleDeleteMass(true)} style={{opacity:confirmDeleteText !== 'APAGAR' ? 0.5 : 1}}>Sim, Excluir Toda a Base</button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL: Importação de Parceiros */}
            {modalConfig.type === 'importacao' && (
                <div className="go-modal-backdrop">
                    <div className="go-modal-window">
                        <div className="go-modal-header"><h3>Importar Dados</h3><button className="go-close-btn" onClick={() => setModalConfig({type:null})}><FaTimesCircle/></button></div>
                        <div className="go-modal-body">
                            <div className="go-upload-container">
                                <label className="go-upload-box"><FaFileExcel size={24}/> <span>Selecionar Planilha (.xlsx)</span><input type="file" onChange={e => setArquivoImportacao(e.target.files[0])} hidden/>{arquivoImportacao && <small style={{color:'var(--go-primary)',marginTop:10}}>{arquivoImportacao.name}</small>}</label>
                                <div className="go-import-hint"><FaExclamationTriangle/><span>Na coluna <strong>Telefone</strong> você pode informar até {MAX_TELEFONES} números separados por vírgula. O <strong>primeiro</strong> será o prioritário.</span></div>
                            </div>
                        </div>
                        <div className="go-modal-footer"><button onClick={submitImportacao} className="go-btn-new primary">Enviar Arquivo</button></div>
                    </div>
                </div>
            )}

            {/* MODAL: Importação de Manutenções */}
            {modalConfig.type === 'importacao_manutencao' && (
                <div className="go-modal-backdrop">
                    <div className="go-modal-window">
                        <div className="go-modal-header"><h3>Importar Manutenções</h3><button className="go-close-btn" onClick={() => setModalConfig({type:null})}><FaTimesCircle/></button></div>
                        <div className="go-modal-body">
                            <div className="go-upload-container">
                                <label className="go-upload-box"><FaFileExcel size={24}/> <span>Selecionar Planilha (.xlsx)</span><input type="file" accept=".xlsx,.xls" onChange={e => { setArquivoImportManut(e.target.files[0]||null); setImportManutResult(null); }} hidden/>{arquivoImportManut && <small style={{color:'var(--go-primary)',marginTop:10}}>{arquivoImportManut.name}</small>}</label>
                                <div className="go-import-hint"><FaExclamationTriangle/><span>A coluna <strong>Veículo</strong> deve ter a placa no formato <strong>PLACA - Nome</strong>. Reimportar o mesmo arquivo <strong>não duplica</strong>.</span></div>
                                {importManutResult && (
                                    <div className="go-import-result">
                                        <div className="go-import-stats">
                                            <div className="go-import-stat ok"><strong>{importManutResult.importadas??0}</strong><span>Importadas</span></div>
                                            <div className="go-import-stat ok"><strong>{importManutResult.atualizadas??0}</strong><span>Atualizadas</span></div>
                                            <div className="go-import-stat warn"><strong>{importManutResult.duplicadas??0}</strong><span>Sem alteração</span></div>
                                            <div className="go-import-stat muted"><strong>{(importManutResult.nao_encontradas?.length??0)+(importManutResult.sem_veiculo??0)}</strong><span>Ignoradas</span></div>
                                        </div>
                                        {importManutResult.nao_encontradas?.length > 0 && (
                                            <div className="go-import-naoenc"><strong>{importManutResult.nao_encontradas.length}</strong> placa(s) não encontrada(s):<div className="placas">{importManutResult.nao_encontradas.map((p,i) => <span className="placa-chip" key={i}>{p}</span>)}</div></div>
                                        )}
                                        {importManutResult.erros?.length > 0 && (
                                            <div className="go-import-erros"><strong>{importManutResult.erros.length}</strong> linha(s) com erro:<ul>{importManutResult.erros.slice(0,12).map((e,i) => <li key={i}>{e}</li>)}</ul>{importManutResult.erros.length > 12 && <small>…e mais {importManutResult.erros.length-12}.</small>}</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="go-modal-footer">
                            <button className="go-btn-new ghost" onClick={() => setModalConfig({type:null})}>Fechar</button>
                            <button onClick={submitImportManutencao} className="go-btn-new primary" disabled={importingManut||!arquivoImportManut}>
                                {importingManut ? <><FaSpinner className="go-spin-icon"/> Importando...</> : <><FaUpload/> Enviar Arquivo</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}